import React, { useState } from 'react';

interface AdminSidebarProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ activeSection, onSectionChange }) => {
  const [aboutExpanded, setAboutExpanded] = useState(true);
  const [createExpanded, setCreateExpanded] = useState(true);

  const mainNavItems = [
    { id: 'services', label: 'Services', icon: '' },
    { id: 'stake', label: 'Stake', icon: '' },
    { id: 'labs', label: 'Labs', icon: '' },
    { id: 'blog', label: 'Blog', icon: '' },
  ];

  const aboutItems = [
    { id: 'about-team', label: 'Team', icon: '' },
    { id: 'about-innovation', label: 'Innovation', icon: '' },
    { id: 'about-numbers', label: 'Numbers', icon: '' },
    { id: 'about-contact', label: 'Contact Us', icon: '' },
    { id: 'about-jobs', label: 'Jobs', icon: '' },
    { id: 'about-trusted-teams', label: 'Trusted By', icon: '' },
  ];

  const createItems = [
    { id: 'create-like-stake', label: 'Like Stake', icon: '' },
    { id: 'create-info-page', label: 'Info Page', icon: '' },
  ];

  const NavButton = ({ id, label, icon, isActive = false }: { id: string; label: string; icon: string; isActive?: boolean }) => (
    <button
      onClick={() => onSectionChange(id)}
      className="w-full flex items-center px-3 py-3 rounded-xl text-left transition-all duration-200 group border-0 cursor-pointer"
      style={
        isActive
          ? {
              background: 'var(--dusk-admin-active)',
              color: '#fff',
              boxShadow: '0 0 20px rgba(208, 0, 104, 0.28)',
            }
          : {
              background: 'transparent',
              color: 'var(--dusk-muted)',
            }
      }
      onMouseEnter={(e) => {
        if (!isActive) {
          e.currentTarget.style.background = 'var(--dusk-admin-hover)';
          e.currentTarget.style.color = 'var(--dusk-ink)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isActive) {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = 'var(--dusk-muted)';
        }
      }}
    >
      <span className={`mr-3 text-lg ${isActive ? 'scale-110' : 'group-hover:scale-110'} transition-transform`}>{icon}</span>
      <span className={`font-medium ${isActive ? 'font-semibold' : ''}`}>{label}</span>
    </button>
  );

  const ExpandableSection = ({
    title,
    icon,
    items,
    isExpanded,
    onToggle,
  }: {
    title: string;
    icon: string;
    items: typeof aboutItems;
    isExpanded: boolean;
    onToggle: () => void;
  }) => {
    const sectionActive = items.some((item) => activeSection === item.id);
    return (
      <div>
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-between px-3 py-3 rounded-xl text-left transition-all duration-200 group border-0 cursor-pointer"
          style={
            sectionActive
              ? {
                  background: 'var(--dusk-admin-active)',
                  color: '#fff',
                  boxShadow: '0 0 20px rgba(208, 0, 104, 0.28)',
                }
              : {
                  background: 'transparent',
                  color: 'var(--dusk-muted)',
                }
          }
        >
          <div className="flex items-center">
            <span className="mr-3 text-lg">{icon}</span>
            <span className="font-medium">{title}</span>
          </div>
          <svg
            className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" strokeWidth={2} />
          </svg>
        </button>

        {isExpanded && (
          <div className="mt-2 ml-2 space-y-2 pl-3" style={{ borderLeft: '1px solid var(--dusk-line)' }}>
            {items.map((item) => {
              const active = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => onSectionChange(item.id)}
                  className="w-full flex items-center px-3 py-2 rounded-lg text-left transition-all duration-200 text-sm group border-0 cursor-pointer"
                  style={
                    active
                      ? {
                          background: 'rgba(208, 0, 104, 0.16)',
                          color: 'var(--dusk-amber)',
                          fontWeight: 600,
                        }
                      : {
                          background: 'transparent',
                          color: 'var(--dusk-faint)',
                        }
                  }
                >
                  <span className="mr-2 group-hover:scale-125 transition-transform">{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="w-64 flex-shrink-0 overflow-y-auto scrollbar-hide"
      style={{
        height: 'calc(100vh - 73px)',
        backgroundColor: 'rgba(10, 9, 8, 0.72)',
        backdropFilter: 'blur(10px)',
        borderRight: '1px solid var(--dusk-line)',
      }}
    >
      <div className="p-6">
        <h2
          className="text-xs font-semibold mb-6 uppercase tracking-widest m-0"
          style={{ color: 'var(--dusk-faint)' }}
        >
          Content Management
        </h2>

        <nav className="space-y-3">
          <div className="space-y-2">
            {mainNavItems.map((item) => (
              <NavButton
                key={item.id}
                id={item.id}
                label={item.label}
                icon={item.icon}
                isActive={activeSection === item.id}
              />
            ))}
          </div>

          <div className="my-4" style={{ borderTop: '1px solid var(--dusk-line)' }} />

          <ExpandableSection
            title="About"
            icon=""
            items={aboutItems}
            isExpanded={aboutExpanded}
            onToggle={() => setAboutExpanded(!aboutExpanded)}
          />

          <ExpandableSection
            title="Create"
            icon=""
            items={createItems}
            isExpanded={createExpanded}
            onToggle={() => setCreateExpanded(!createExpanded)}
          />
        </nav>

        <div className="mt-8 pt-6" style={{ borderTop: '1px solid var(--dusk-line)' }}>
          <p className="text-xs text-center m-0" style={{ color: 'var(--dusk-faint)' }}>
            by DUSKPROOF
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminSidebar;
